import { useEffect, useState } from "react";
import { Users, Send, Loader2, CheckCircle2, ChevronRight, ToggleLeft, ToggleRight } from "lucide-react";
import api, { formatApiError } from "../../api";

export default function ProgrammesAdmin() {
  const [progs, setProgs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [invitees, setInvitees] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [settings, setSettings] = useState(null);   // Mercury super-admin toggle list
  const [toggleBusy, setToggleBusy] = useState(null);   // programme_key while patching

  const load = () => api.get("/admin/programs").then((r) => setProgs(r.data)).catch((e) => setErr(formatApiError(e)));
  const loadSettings = () => api.get("/admin/programmes/settings").then((r) => setSettings(r.data)).catch(() => setSettings([]));
  useEffect(() => { load(); loadSettings(); }, []);
  useEffect(() => {
    if (!selected) { setInvitees(null); return; }
    api.get(`/admin/programs/${selected}/invitees`).then((r) => setInvitees(r.data)).catch(() => setInvitees([]));
  }, [selected]);

  const toggleProgramme = async (key, next) => {
    setToggleBusy(key); setErr("");
    try {
      await api.patch("/admin/programmes/settings", { key, enabled: next });
      await loadSettings();
      await load();
    } catch (e) { setErr(formatApiError(e)); } finally { setToggleBusy(null); }
  };

  const invite = async () => {
    const emails = csvText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!emails.length) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post(`/admin/programs/${selected}/invite`, { emails });
      setResult(r.data);
      setCsvText("");
      await load();
      const iv = await api.get(`/admin/programs/${selected}/invitees`);
      setInvitees(iv.data);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!progs) return <div className="p-8 text-sm text-[#878787]">Loading programmes…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="programmes-admin">
      <div>
        <div className="overline mb-2">ADMIN · PROGRAMMES</div>
        <h1 className="font-heading text-3xl font-bold">Programmes &amp; invitations</h1>
        <p className="text-sm text-[#525252] mt-2 max-w-3xl">
          Each programme is a container. Invite candidates by pasting their email addresses (comma / newline / semicolon separated). Idempotent — re-inviting is a no-op.
        </p>
      </div>

      {/* Mercury-admin only — enable/disable programmes across the platform. */}
      {settings && settings.length > 0 && (
        <div className="card p-6" data-testid="programme-visibility-panel">
          <div className="overline mb-2">MERCURY ADMIN · PROGRAMME VISIBILITY</div>
          <div className="text-sm text-[#525252] mb-4 max-w-3xl">
            Disabling a programme hides it from the client-lead admin (Avni) and from every candidate&apos;s dashboard immediately.
            Existing invitations, content, and assignments are preserved — re-enabling restores everything.
          </div>
          <div className="space-y-2">
            {settings.map((s) => (
              <div key={s.key} className="flex items-center justify-between border border-[#E5E7EB] p-3" data-testid={`prog-toggle-${s.key}`}>
                <div className="min-w-0 pr-4">
                  <div className="font-heading text-sm font-bold">{s.name}</div>
                  <div className="text-xs text-[#878787] mt-0.5 truncate">{s.tagline}</div>
                </div>
                <button
                  onClick={() => toggleProgramme(s.key, !s.enabled)}
                  disabled={toggleBusy === s.key}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition ${
                    s.enabled ? "bg-[#059669] text-white" : "bg-[#E5E7EB] text-[#525252]"
                  } disabled:opacity-50`}
                  data-testid={`prog-toggle-btn-${s.key}`}
                >
                  {toggleBusy === s.key ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : s.enabled ? (
                    <><ToggleRight size={14} /> Enabled</>
                  ) : (
                    <><ToggleLeft size={14} /> Disabled</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="programmes-grid">
        {progs.map((p) => (
          <div key={p.key} className="card p-6" data-testid={`prog-card-${p.key}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center text-white font-bold" style={{ background: p.cover_hex }}>
                {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <button onClick={() => setSelected(p.key)} className="btn btn-secondary text-xs" data-testid={`prog-manage-${p.key}`}>
                Manage <ChevronRight size={12} />
              </button>
            </div>
            <div className="font-heading text-lg font-bold mb-1">{p.name}</div>
            <div className="overline mb-3">{p.duration_min} min · {p.sections.length} sections</div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div><div className="text-2xl font-black">{p.invitees}</div><div className="text-[10px] uppercase tracking-wider text-[#878787]">Invitees</div></div>
              <div><div className="text-2xl font-black">{p.in_progress}</div><div className="text-[10px] uppercase tracking-wider text-[#878787]">In Progress</div></div>
              <div><div className="text-2xl font-black">{p.completed}</div><div className="text-[10px] uppercase tracking-wider text-[#878787]">Completed</div></div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="card p-6 space-y-4" data-testid="prog-manage-panel">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-1">MANAGING</div>
              <h2 className="font-heading text-xl font-bold">{progs.find((p) => p.key === selected)?.name}</h2>
            </div>
            <button onClick={() => setSelected(null)} className="text-[#878787] hover:text-[#0A0A0A] text-xs">Close ✕</button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-2">Bulk invite (paste emails — comma, newline or semicolon separated)</div>
            <textarea
              rows={4}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="arjun.reddy@vi.com, sneha.pillai@vi.com,&#10;another@vi.com"
              className="w-full text-sm border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:border-[#EE2737] font-mono"
              data-testid="prog-invite-textarea"
            />
            <button onClick={invite} disabled={busy || !csvText.trim()} className="btn btn-primary mt-2 text-xs disabled:opacity-40" data-testid="prog-invite-submit">
              {busy ? <><Loader2 size={12} className="animate-spin" /> Inviting…</> : <><Send size={12} /> Invite</>}
            </button>
            {err && <div className="text-xs text-[#EE2737] mt-2">{err}</div>}
            {result && (
              <div className="mt-3 text-xs border border-[#E5E7EB] p-3 bg-[#F9FAFB]" data-testid="prog-invite-result">
                <div><CheckCircle2 size={12} className="inline text-[#059669]" /> Created: <strong>{result.created.length}</strong></div>
                {result.skipped.length > 0 && (
                  <div className="mt-1">Skipped: {result.skipped.map((s) => `${s.email} (${s.reason})`).join(", ")}</div>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-2">
              <Users size={12} /> Invitees ({invitees ? invitees.length : "…"})
            </div>
            <div className="border border-[#E5E7EB] max-h-80 overflow-auto" data-testid="prog-invitees-list">
              {invitees && invitees.length > 0 ? (
                <table className="w-full text-xs">
                  <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Invited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitees.map((i) => (
                      <tr key={i.id} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2 font-mono">{i.email || "—"}</td>
                        <td className="px-3 py-2">{i.name || "—"}</td>
                        <td className="px-3 py-2 uppercase text-[10px]">{i.status}</td>
                        <td className="px-3 py-2 text-[#878787]">{i.invited_at ? new Date(i.invited_at).toLocaleDateString() : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-xs text-[#878787]">No invitees yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
